import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBondChatSocket } from "../../redux/bondchat/generalChatSingleton";
import {
  fetchChatEntry,
  fetchMessagesAfter,
  fetchChatChallenges,
  fetchChatChallengesBefore,
  fetchChatPolls,
  fetchChatPollsBefore,
  fetchMessagesBefore,
  sendMessage,
  likeMessage,
  reactToMessage,
  emitLoungeTyping,
  createPollMessage,
  createChatChallenge,
  joinChatChallenge,
  votePollMessage,
  markAsReadMessage,
  persistReadCursor,
  reportMessageAction,
  deleteMessageAction,
  setLoungeInForeground,
  initializeSocket,
} from "../../redux/bondchat/generalChatSlice";
import { selectAppUser } from "../../redux/appAuthSlice";
import { buildReplyTarget } from "../../utils/bondchat/replyHelpers";
import {
  CHALLENGE_INVOLVEMENT_FILTERS,
  matchesChallengeInvolvement,
} from "../../utils/bondchat/chatChallengeHelpers";
import MessageTimeline from "./MessageTimeline";
import ChatComposer from "./ChatComposer";
import TypingIndicator from "./TypingIndicator";

const LoungePanel = ({ isActive = true }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const {
    messages,
    loading,
    hasMore,
    connected,
    typingUsers,
    lastReadMessageId,
    challengeMessages,
    challengesHasMore,
    challengesLoading,
    pollMessages,
    pollsHasMore,
    pollsLoading,
    entryLoaded,
    hasNewer,
    unreadCount,
  } = useSelector((s) => s.generalChat);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reportedId, setReportedId] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [feedFilter, setFeedFilter] = useState('all');
  const [challengeInvolvement, setChallengeInvolvement] = useState("all");

  useEffect(() => {
    if (!user?._id || !user?.name) return;
    dispatch(initializeSocket(user._id));
    dispatch(fetchChatEntry({ limit: 50, context: 10, userId: user._id }));
  }, [user?._id, user?.name, dispatch]);

  useEffect(() => {
    dispatch(setLoungeInForeground(isActive));
    return () => dispatch(setLoungeInForeground(false));
  }, [isActive, dispatch]);

  useEffect(() => {
    if (!user?._id || !isActive) return undefined;
    const emitEnter = () => {
      const s = getBondChatSocket();
      if (s?.connected) s.emit("enterLounge", { userId: user._id });
    };
    const emitLeave = () => {
      const s = getBondChatSocket();
      if (s?.connected) s.emit("leaveLounge", { userId: user._id });
    };
    emitEnter();
    const s = getBondChatSocket();
    s?.once?.("connect", emitEnter);
    return () => emitLeave();
  }, [isActive, user?._id, connected]);


  const isChallengesFeed = feedFilter === "challenges";
  const isPollsFeed = feedFilter === "polls";
  const feedMessages = useMemo(() => {
    if (isChallengesFeed) {
      return challengeMessages.filter((msg) =>
        matchesChallengeInvolvement(msg, challengeInvolvement, user?._id)
      );
    }
    if (isPollsFeed) return pollMessages;
    return messages;
  }, [
    isChallengesFeed,
    isPollsFeed,
    challengeMessages,
    challengeInvolvement,
    pollMessages,
    messages,
    user?._id,
  ]);
  const feedHasMore = isChallengesFeed
    ? challengesHasMore
    : isPollsFeed
      ? pollsHasMore
      : hasMore;
  const feedLoading = isChallengesFeed
    ? challengesLoading
    : isPollsFeed
      ? pollsLoading
      : loading || (!entryLoaded && messages.length === 0);

  const handleFeedFilterChange = (next) => {
    setFeedFilter(next);
    if (next !== "challenges") {
      setChallengeInvolvement("all");
    }
    if (next === "challenges") {
      dispatch(fetchChatChallenges({ limit: 30, mine: challengeInvolvement }));
    } else if (next === "polls") {
      dispatch(fetchChatPolls({ limit: 30 }));
    }
  };

  const handleChallengeInvolvementChange = (next) => {
    setChallengeInvolvement(next);
    dispatch(fetchChatChallenges({ limit: 30, mine: next }));
  };

  const loadOlder = async () => {
    if (feedLoading || loadingMore || !feedHasMore) return;
    if (feedFilter === "challenges") {
      const beforeId = challengeMessages[0]?._id;
      if (!beforeId) return;
      setLoadingMore(true);
      try {
        await dispatch(
          fetchChatChallengesBefore({
            beforeId,
            limit: 20,
            mine: challengeInvolvement,
          })
        ).unwrap();
      } finally {
        setLoadingMore(false);
      }
      return;
    }
    if (feedFilter === "polls") {
      const beforeId = pollMessages[0]?._id;
      if (!beforeId) return;
      setLoadingMore(true);
      try {
        await dispatch(fetchChatPollsBefore({ beforeId, limit: 20 })).unwrap();
      } finally {
        setLoadingMore(false);
      }
      return;
    }
    const beforeId = messages[0]?._id;
    if (!beforeId) return;
    setLoadingMore(true);
    try {
      await dispatch(fetchMessagesBefore({ beforeId, limit: 20 })).unwrap();
    } finally {
      setLoadingMore(false);
    }
  };

  const loadNewer = async () => {
    if (feedFilter !== "all" || loadingMore || !hasNewer) return;
    const afterId = messages[messages.length - 1]?._id;
    if (!afterId) return;
    setLoadingMore(true);
    try {
      await dispatch(fetchMessagesAfter({ afterId, limit: 30 })).unwrap();
    } finally {
      setLoadingMore(false);
    }
  };

  const markRead = useCallback(
    (ids, cursor = null) => {
      if (!user?._id) return;
      if (ids?.length) {
        dispatch(
          markAsReadMessage({
            messageIds: ids,
            userId: user._id,
            lastReadMessageId: cursor,
          })
        );
      } else if (cursor) {
        dispatch(persistReadCursor({ userId: user._id, messageId: cursor }));
      }
    },
    [dispatch, user?._id]
  );

  const handleTyping = useCallback(
    (isTyping) => {
      if (!user?._id) return;
      dispatch(emitLoungeTyping({ userId: user._id, name: user?.name, isTyping }));
    },
    [dispatch, user?._id, user?.name]
  );

  const handleReply = useCallback((msg) => {
    const target = buildReplyTarget(msg);
    if (target) setReplyTarget(target);
  }, []);

  if (!user?._id) {
    return (
      <div className="bc-panel-gate">
        <p>Sign in to join the Lounge.</p>
      </div>
    );
  }

  return (
    <div className="bc-panel">
      <div className="bc-feed-filter">
        <div className="bc-feed-filter-row" role="tablist" aria-label="Lounge feed">
          <button
            type="button"
            role="tab"
            aria-selected={feedFilter === "all"}
            className={`bc-feed-filter-chip${feedFilter === "all" ? " is-active" : ""}`}
            onClick={() => handleFeedFilterChange("all")}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={feedFilter === "challenges"}
            className={`bc-feed-filter-chip${feedFilter === "challenges" ? " is-active" : ""}`}
            onClick={() => handleFeedFilterChange("challenges")}
          >
            Challenges
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={feedFilter === "polls"}
            className={`bc-feed-filter-chip${feedFilter === "polls" ? " is-active" : ""}`}
            onClick={() => handleFeedFilterChange("polls")}
          >
            Polls
          </button>
        </div>
        {isChallengesFeed ? (
          <div
            className="bc-feed-filter-row"
            role="tablist"
            aria-label="Challenge involvement"
          >
            {CHALLENGE_INVOLVEMENT_FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={challengeInvolvement === option.id}
                className={`bc-feed-filter-chip is-sub${
                  challengeInvolvement === option.id ? " is-active" : ""
                }`}
                onClick={() => handleChallengeInvolvementChange(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <MessageTimeline
        messages={feedMessages}
        userId={user._id}
        loading={feedLoading}
        loadingMore={loadingMore}
        hasMore={feedHasMore}
        onLoadOlder={loadOlder}
        onLoadNewer={loadNewer}
        hasNewer={feedFilter === "all" && hasNewer}
        emptyText={
          isChallengesFeed
            ? challengeInvolvement === "created"
              ? "You haven't posted a lounge challenge yet."
              : challengeInvolvement === "joined"
                ? "You haven't countered a lounge challenge yet."
                : "No open challenges right now. Finished matches drop off after 24 hours."
            : isPollsFeed
              ? "No polls yet. Tap the poll icon to ask the crowd."
              : "Be the first to drop a take — the crowd is waiting."
        }
        feedKey={`${feedFilter}:${challengeInvolvement}`}
        pinToLatest={feedFilter !== "all"}
        onLike={(id) => dispatch(likeMessage({ messageId: id, userId: user._id }))}
        onReact={(id, emoji) => dispatch(reactToMessage({ messageId: id, userId: user._id, emoji }))}
        onReply={handleReply}
        onCounterChallenge={(messageId, stake = 500) => {
          const stakeAmount = Math.max(500, Number(stake) || 500);
          return dispatch(
            joinChatChallenge({
              userId: user._id,
              messageId,
              stake: stakeAmount,
              idempotencyKey: `counter-${messageId}-${user._id}-${stakeAmount}-${Date.now()}`,
            })
          );
        }}
        onVotePoll={(id, vote) => dispatch(votePollMessage({ messageId: id, userId: user._id, vote }))}
        onReport={setReportedId}
        onDelete={(id) => dispatch(deleteMessageAction({ messageId: id, userId: user._id }))}
        onMarkRead={markRead}
        lastReadMessageId={feedFilter === "all" ? lastReadMessageId : null}
        readStateLoaded={feedFilter !== "all" || entryLoaded}
        entryUnreadCount={feedFilter === "all" ? unreadCount : 0}
      />
      <TypingIndicator typingUsers={typingUsers} />
      <ChatComposer
        enableChatChallenges
        enableFixtureTags
        replyTarget={replyTarget}
        onCancelReply={() => setReplyTarget(null)}
        onTyping={handleTyping}
        onCreateChallenge={(payload) => {
          const tempId = `challenge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          return dispatch(createChatChallenge({ userId: user._id, tempId, ...payload }));
        }}
        onSendMessage={(text, refs, reply) => {
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          dispatch(
            sendMessage({
              userId: user._id,
              message: text,
              tempId,
              fixtureRefs: refs,
              replyTo: reply || replyTarget,
            })
          );
          setReplyTarget(null);
        }}
        onCreatePoll={(question, refs) => {
          const tempId = `poll-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
          dispatch(createPollMessage({ userId: user._id, question, tempId, fixtureRefs: refs }));
        }}
        reportedMessageId={reportedId}
        onClearReportTarget={() => setReportedId(null)}
        onSubmitReport={(id, reason) => {
          dispatch(reportMessageAction({ messageId: id, userId: user._id, reason }));
          setReportedId(null);
        }}
      />
    </div>
  );
};

export default LoungePanel;
